import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import moment from 'moment';
import { useCallback, useMemo } from 'react';

import type {
  RecordDetailsForm,
  UseEmployeeProps,
} from '@app/components/RecordModal/components/RecordDetails/types';
import {
  generateHourOptions,
  generateMinuteOptions,
  generateTimeOptions,
} from '@app/components/RecordModal/components/RecordDetails/utils';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { useJournalAccessRights } from '@app/hooks';
import { useAppSelector } from '@app/store';
const { useFormInstance, useWatch } = Form;

export const useEmployee = ({
  disabled,
  isFetchingGetClientServicesByEmployeeId,
  isLoadingGetClientServicesByEmployeeId,
}: UseEmployeeProps) => {
  const { record, mode, isLoading, isOpen, warnings } =
    useAppSelector(selectRecordModal);
  const { canOnlyView } = useJournalAccessRights();

  const form = useFormInstance<RecordDetailsForm>();

  const { setFieldValue, getFieldValue, setFieldsValue, setFields } = form;
  const formValues = useWatch([], form);

  const timeOptions = useMemo(generateTimeOptions, []);
  const hourOptions = useMemo(generateHourOptions, []);
  const minuteOptions = useMemo(generateMinuteOptions, []);

  const lockedEmployee = useMemo(
    () => formValues?.IsLocked || false,
    [formValues]
  );

  const LockIcon = formValues?.IsLocked ? UnlockOutlined : LockOutlined;

  const handleChangeTimes = useCallback(
    (type: 'TimeStart' | 'TimeEnd') => () => {
      const timeStart = getFieldValue('TimeStart');
      const timeEnd = getFieldValue('TimeEnd');
      const durationHour = getFieldValue('DurationHour');
      const durationMinute = getFieldValue('DurationMinute');

      if (type === 'TimeStart') {
        setFieldsValue({
          TimeEnd: moment(timeStart, 'HH:mm')
            .add(durationHour, 'hours')
            .add(durationMinute, 'minutes')
            .format('HH:mm'),
        });
      }

      if (type === 'TimeEnd') {
        const durationMinutes = moment(timeEnd, 'HH:mm').diff(
          moment(timeStart, 'HH:mm'),
          'minutes'
        );
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes - hours * 60;
        setFieldsValue({
          DurationHour: hours >= 0 ? hours : 0,
          DurationMinute: minutes >= 0 ? minutes : 0,
        });
      }
    },
    [getFieldValue, setFieldsValue]
  );

  const handleChangeDate = useCallback(() => {
    const DurationHour = getFieldValue('DurationHour');
    const DurationMinute = getFieldValue('DurationMinute');
    const timeStart = getFieldValue('TimeStart');

    const timeEnd = moment(timeStart, 'HH:mm')
      .add(DurationHour, 'hours')
      .add(DurationMinute, 'minutes');

    setFieldsValue({
      TimeEnd: timeEnd.format('HH:mm'),
    });
  }, [getFieldValue, setFieldsValue]);

  const disabledEmployee = useMemo(
    () =>
      isFetchingGetClientServicesByEmployeeId ||
      isLoadingGetClientServicesByEmployeeId ||
      disabled ||
      lockedEmployee ||
      (mode === 'edit' && canOnlyView) ||
      record?.IsDeleted,
    [
      isFetchingGetClientServicesByEmployeeId,
      isLoadingGetClientServicesByEmployeeId,
      disabled,
      lockedEmployee,
      record,
      canOnlyView,
      mode,
    ]
  );

  return {
    timeOptions,
    hourOptions,
    minuteOptions,
    LockIcon,
    lockedEmployee,
    disabledEmployee,
    setFieldValue,
    handleChangeTimes,
    handleChangeDate,
    getFieldValue,
    setFields,
    isLoading,
    isOpen,
    warnings,
  };
};
